import Global from "../../../components/Atomos/Icons/Global";
import { getBandForPercent } from "./networkingMapBands";
import type { NetworkingMapNode } from "./networkingMapTypes";

type NetworkingMapPersonMiniPanelProps = {
  node: NetworkingMapNode["data"];
  x: number;
  y: number;
  onOpenProfile: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
};

function ExternalLinkIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className="shrink-0"
    >
      <path
        d="M11 2.667H5.333v1.333H9.39L3.333 9.753l.914.914 6.057-6.057v4.057H11V2.667Z"
        fill="currentColor"
      />
      <path
        d="M12.667 12.667H3.333V4H2v9.333h10.667V12.667Z"
        fill="currentColor"
      />
    </svg>
  );
}

export default function NetworkingMapPersonMiniPanel({
  node,
  x,
  y,
  onOpenProfile,
  onMouseEnter,
  onMouseLeave,
}: NetworkingMapPersonMiniPanelProps) {
  const {
    name,
    role,
    firm,
    avatar,
    affinityPercent,
    jurisdictionLabel,
  } = node;
  const levelLabel =
    affinityPercent != null
      ? getBandForPercent(affinityPercent).label
      : null;

  return (
    <div
      className="pointer-events-auto absolute z-40 flex w-[min(100%,280px)] flex-col items-start gap-2.5 rounded-lg border border-[#F6F6F6] bg-white p-4 shadow-[0_2px_16px_0_rgba(31,31,31,0.12),0_1px_16px_0_rgba(31,31,31,0.06)]"
      style={{ left: x, top: y }}
      role="dialog"
      aria-label={`${name} preview`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="flex w-full items-start gap-3">
        <div
          className="h-12 w-12 shrink-0 rounded-full bg-slate-300 bg-cover bg-center"
          style={avatar ? { backgroundImage: `url("${avatar}")` } : undefined}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <p className="font-['Roboto'] text-sm font-semibold leading-5 text-gray-font">
            {name}
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {levelLabel && (
              <span className="rounded-lg bg-[#BC3323] px-2 py-0.5 font-['Roboto'] text-xs font-medium leading-4 text-white">
                {levelLabel}
              </span>
            )}
            {role && (
              <span className="rounded-lg bg-[#EBEBEB] px-2 py-0.5 font-['Roboto'] text-xs font-medium leading-4 text-gray-font">
                {role}
              </span>
            )}
          </div>
        </div>
      </div>

      {firm && (
        <p className="flex w-full items-start gap-2 font-['Roboto'] text-sm leading-5 text-gray-font">
          <Global className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <span className="min-w-0 break-words">{firm}</span>
        </p>
      )}

      {jurisdictionLabel && (
        <p className="flex w-full flex-wrap items-center gap-1 font-['Roboto'] text-sm leading-5 text-gray-font">
          <Global className="h-4 w-4 shrink-0 text-gray-font" />
          <span className="font-semibold">Jurisdiction:</span>
          <span>{jurisdictionLabel}</span>
        </p>
      )}

      <div className="h-px w-full bg-[#F6F6F6]" />

      <button
        type="button"
        onClick={onOpenProfile}
        className="flex w-full items-center justify-center gap-2 rounded bg-primary px-4 py-2.5 font-['Roboto'] text-xs font-semibold uppercase tracking-wide text-white transition-opacity hover:opacity-90"
      >
        Open profile
        <ExternalLinkIcon />
      </button>
    </div>
  );
}
